import React, { useState } from 'react';
import { TrendingUp, Zap, Target, AlertCircle } from 'lucide-react';

function OptimizationPanel({ campaignId = '123', campaignName = 'Campanha Exemplo', token = 'demo-token' }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  
  const analyzePerformance = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch('https://growly-test-production.up.railway.app/api/optimizations/analyze', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ campaignId })
      });
      
      if (!response.ok) {
        throw new Error('Erro ao analisar campanha');
      }
      
      const data = await response.json();
      setAnalysis(data);
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message || 'Erro ao analisar campanha');
    } finally {
      setLoading(false);
    }
  };
  
  const applyOptimization = async (optimization) => {
    setApplying(true);
    setError(null);
    
    try {
      const response = await fetch('https://growly-test-production.up.railway.app/api/optimizations/apply', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          campaignId,
          optimizationId: optimization.id,
          type: optimization.titulo
        })
      });
      
      if (!response.ok) {
        throw new Error('Erro ao aplicar otimização');
      }
      
      const data = await response.json();
      
      // Atualizar UI para mostrar otimização aplicada
      setAnalysis(prev => ({
        ...prev,
        optimizations: prev.optimizations.map(opt => 
          opt.id === optimization.id 
            ? { ...opt, applied: true } 
            : opt
        )
      }));
      
    } catch (err) {
      console.error('Erro:', err);
      setError(err.message || 'Erro ao aplicar otimização');
    } finally {
      setApplying(false);
    }
  };
  
  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <Zap className="text-yellow-500" size={24} />
            <h2 className="text-2xl font-bold text-gray-800">
              Otimizações Automáticas - {campaignName}
            </h2>
          </div>
          
          <button
            onClick={analyzePerformance}
            disabled={loading}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
              loading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            <Target size={20} />
            {loading ? 'Analisando...' : 'Analisar Performance'}
          </button>
        </div>
        
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="text-red-600 flex-shrink-0" size={20} />
            <p className="text-red-800">{error}</p>
          </div>
        )}
        
        {analysis?.isSimulation && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800">
              📊 Dados simulados para demonstração (conta em modo desenvolvimento)
            </p>
          </div>
        )}
        
        {analysis?.performance && (
          <div className="mb-6 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-700 mb-4">
              Métricas de Performance:
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-sm text-gray-600">Impressões</p>
                <p className="text-2xl font-bold text-gray-800">
                  {analysis.performance.impressions.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-sm text-gray-600">Cliques</p>
                <p className="text-2xl font-bold text-blue-600">
                  {analysis.performance.clicks.toLocaleString()}
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-sm text-gray-600">CTR</p>
                <p className="text-2xl font-bold text-green-600">
                  {analysis.performance.ctr}%
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-sm text-gray-600">CPC</p>
                <p className="text-2xl font-bold text-orange-600">
                  R$ {analysis.performance.cpc}
                </p>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow">
                <p className="text-sm text-gray-600">Conversões</p>
                <p className="text-2xl font-bold text-red-600">
                  {analysis.performance.conversions}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {analysis?.optimizations && analysis.optimizations.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="text-green-500" size={24} />
              <h3 className="text-xl font-semibold text-gray-800">
                Sugestões de Otimização:
              </h3>
            </div>
            
            <div className="space-y-4">
              {analysis.optimizations.map((opt) => (
                <div 
                  key={opt.id} 
                  className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200"
                >
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-blue-700 mb-2">
                        {opt.titulo}
                      </h4>
                      <p className="text-gray-700 mb-3">
                        {opt.descricao}
                      </p>
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-semibold">
                        Impacto esperado: {opt.impacto_esperado}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => applyOptimization(opt)}
                      disabled={applying || opt.applied}
                      className={`px-6 py-2 rounded-lg font-semibold transition-all whitespace-nowrap ${
                        opt.applied
                          ? 'bg-green-500 text-white cursor-default'
                          : applying
                          ? 'bg-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                      }`}
                    >
                      {opt.applied ? 'Aplicada ✓' : 'Aplicar'}
                    </button>
                  </div>
                  
                  {opt.applied && (
                    <div className="mt-4">
                      <div className="w-full bg-green-200 rounded-full h-2">
                        <div className="bg-green-600 h-2 rounded-full w-full"></div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {!loading && !analysis && (
          <div className="text-center py-12 text-gray-500">
            <Target size={48} className="mx-auto mb-4 opacity-50" />
            <p className="text-lg">Clique em "Analisar Performance" para começar</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default OptimizationPanel;